/* ============================================================
   PDP  –  pdp.js: the product page shared by gardin and solskydd.

   Behaviour for the blocks the two templates add on top of the theme's
   own product page. Everything the theme already owns - the media slider,
   thumbnails, zoom, variant picker, quantity input and the made-to-measure
   customizer - is left alone; this file only wires the new blocks and
   listens to the theme's events.

   The templates are a visual upgrade: how a product is bought is the old
   page's. The customizer (another company's code) is never called,
   patched or intercepted from here. One addition to buying: a variant
   change empties the width and height at the click (variantGuard).

   What only one family has lives in its own file, loaded before this one
   by pdp-product-template.liquid where the section needs it, and
   registered on window.HastaPDP for init() below to call:
     pdp-measure.js      the height spans, the line under the measurements
                         and the whole-number fields (solskydd)
     pdp-accessories.js  the accessory ranges and their quick add (gardin)

   All hooks are data-attributes so markup and behaviour stay separate.
   ============================================================ */

(function () {
  'use strict';


  var money = function (cents) {
    if (window.Woolman && Woolman.Utils && Woolman.Utils.formatMoney) {
      return Woolman.Utils.formatMoney(cents);
    }
    return (cents / 100).toFixed(0) + ' kr';
  };


  // Shared with pdp-measure.js and pdp-accessories.js. They load before
  // this file and register what they add (see init); they look these up
  // when they run, never while loading.
  var PDP = window.HastaPDP = window.HastaPDP || {};
  PDP.money = money;
  PDP.debounce = debounce;

  /* ---------- equal-width variant pills -----------------------
     The stylesheet sizes the pills from --rd-seg-w with a hardcoded
     fallback. Without this the fallback wins, so the pills are a fixed
     width regardless of their labels - fine for "132 cm", too narrow for a
     longer value. Measure the widest label's natural width and lock every
     pill to it, so the rows stay equal length and nothing clips.
     Re-measured on resize, once webfonts land, and after a variant change,
     because the theme re-renders the option labels by id.          */
  function segmentWidth(root) {
    var panel = root.querySelector('.rd-options');
    if (!panel) return;

    function measure() {
      // colour chips and icon tiles size themselves (pdp-solskydd.css) -
      // measured, they stretched every pill. Rows turned into a dropdown are
      // left out too: their labels are hidden (optionSelects below)
      var pills = panel.querySelectorAll('.product-form__input:not(.rd-as-select) label:not(.swatch):not(.field__label--image)');
      // a zero-width panel (hidden tab, preview) would measure to nothing and
      // lock the pills to a wrong width - skip and retry on the next resize
      if (!pills.length || !panel.getBoundingClientRect().width) return;

      var previous = panel.style.getPropertyValue('--rd-seg-w');
      panel.style.removeProperty('--rd-seg-w');
      panel.classList.add('rd-mater');            // width: max-content
      var widest = 0;
      Array.prototype.forEach.call(pills, function (l) {
        widest = Math.max(widest, l.getBoundingClientRect().width);
      });
      panel.classList.remove('rd-mater');
      // a real pill is wider than 20px; anything less is a hidden label, and
      // locking to it would collapse every pill on the page
      if (widest > 20) panel.style.setProperty('--rd-seg-w', Math.ceil(widest) + 'px');
      else if (previous) panel.style.setProperty('--rd-seg-w', previous);
    }

    measure();
    window.addEventListener('resize', debounce(measure, 150));
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(measure);
    // optionSelects() asks for a fresh measure before and after every swap
    root.addEventListener('pdp:measure', measure);
    // the theme swaps variant-selects on change, replacing the label elements
    root.addEventListener('change', function (e) {
      if (e.target.closest('.product-form__input')) setTimeout(measure, 0);
    });
    document.addEventListener('variant:changed', function () { setTimeout(measure, 0); });
  }

  function debounce(fn, wait) {
    var t;
    return function () {
      var args = arguments, self = this;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(self, args); }, wait);
    };
  }

  /* ---------- long fixed-size rows -> dropdown ------------------
     A segmented control reads well on one row. Bredd on a stock product
     runs to eleven fixed widths, and eleven pills wrap into a block that
     scans like a table. Where the pills of a row do not fit one line they
     are hidden, and a native <select> drives the same radios, so the
     theme's variant logic sees an ordinary pick.

     Which rows may convert:
     - Variant rows (solskydd, picker_rows): fixed sizes only. The picker
       wrapper carries rd-fixed-sizes when no variant is made to measure
       (blocks/pdp-variant-picker). On a made-to-measure solskydd product
       the rows are spans the shopper then types an exact measure into;
       they never convert and stay pills, as on the old page.
     - "Long option rows as a dropdown" (rd-long-rows on the root,
       skenor-stanger): every product. That page has no spans, so a
       made-to-measure product's rows are ordinary choices (Hotellskena:
       150, 200, 250 cm and Måttillverkad in one row).
     Colour chips never convert.

     Traps, all paid for in the first build of this (lost 2026-09-25):
     - Fit is computed - pill count times --rd-seg-w, less the 1px the
       neighbours overlap - never read off label positions, which all look
       equal before layout settles, so a converted row silently reverted.
     - segmentWidth() leaves converted rows out of its measure; their
       hidden labels would lock every pill on the page to nothing.
     - Unavailable values carry class="disabled", not the property. A
       dropdown has no diagonal, so the option text says it, in the
       theme's own words (products.product.value_unavailable).
     - Never rebuild while the dropdown has focus: replacing the element
       closes an open native list under the shopper's pointer.
     And one of the theme's: the select's own change event must not reach
     <variant-selects>, whose updateSelectionMetadata() looks for an option
     with a selected ATTRIBUTE and throws without one. The select stops it
     and fires change on the radio instead.

     The theme replaces the picker's children on every variant change, so
     a MutationObserver on <variant-selects> re-applies. It watches direct
     children only; the select goes inside a fieldset, so adding it cannot
     re-trigger the observer.                                        */
  function optionSelects(root) {
    var picker = root.querySelector('.rd-options .rd-fixed-sizes variant-selects') ||
      (root.classList.contains('rd-long-rows') ? root.querySelector('.rd-options variant-selects') : null);
    if (!picker) return;
    var unavailable = root.dataset.valueUnavailable || '__VALUE__';
    var waiting = false;
    // the option name whose dropdown had focus when it changed the variant,
    // so the rebuilt one gets the focus back and arrow keys keep working
    var refocus = '';

    function segmentW(fieldset) {
      var w = parseFloat(getComputedStyle(fieldset).getPropertyValue('--rd-seg-w'));
      return w > 20 ? w : 112;   // the stylesheet's own fallback
    }

    function optionName(fieldset) {
      var legend = fieldset.querySelector(':scope > legend');
      return (legend && legend.getAttribute('data-legend')) || '';
    }

    function inUse() {
      var el = document.activeElement;
      return !!(el && el.closest && el.closest('[data-pdp-select]'));
    }

    function revert(fieldset) {
      var wrap = fieldset.querySelector(':scope > [data-pdp-select]');
      if (wrap) wrap.remove();
      fieldset.classList.remove('rd-as-select');
      delete fieldset.dataset.rdSegW;
      fieldset.querySelectorAll('input[type="radio"]').forEach(function (radio) {
        radio.removeAttribute('tabindex');
        radio.removeAttribute('aria-hidden');
      });
    }

    function convert(fieldset, radios) {
      // the pill type, read while the pills still show
      var pill = fieldset.querySelector('label');
      var type = pill ? getComputedStyle(pill) : null;
      var name = optionName(fieldset);

      var wrap = document.createElement('div');
      wrap.className = 'rd-optsel';
      wrap.setAttribute('data-pdp-select', '');
      var select = document.createElement('select');
      select.className = 'rd-optsel__select';
      select.setAttribute('aria-label', name || radios[0].name);
      if (type) {
        select.style.fontFamily = type.fontFamily;
        select.style.fontSize = type.fontSize;
        select.style.letterSpacing = type.letterSpacing;
      }
      radios.forEach(function (radio) {
        var option = document.createElement('option');
        option.value = radio.id;
        option.textContent = radio.classList.contains('disabled')
          ? unavailable.replace('__VALUE__', radio.value)
          : radio.value;
        // the property, never the attribute - getSelectedOptionValues()
        // would read an option[selected] as one of the picked values
        if (radio.checked) option.selected = true;
        select.appendChild(option);
      });
      select.addEventListener('change', function (e) {
        e.stopPropagation();
        var radio = document.getElementById(select.value);
        if (!radio || radio.checked) return;
        refocus = name;
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
      });
      wrap.appendChild(select);
      fieldset.appendChild(wrap);
      fieldset.classList.add('rd-as-select');
      // one control, not two: the radios stay for the theme, out of reach
      radios.forEach(function (radio) {
        radio.setAttribute('tabindex', '-1');
        radio.setAttribute('aria-hidden', 'true');
      });
      if (refocus && refocus === name) {
        refocus = '';
        select.focus({ preventScroll: true });
      }
    }

    function apply() {
      if (inUse()) {
        if (!waiting) {
          waiting = true;
          document.activeElement.addEventListener('blur', function () {
            waiting = false;
            apply();
          }, { once: true });
        }
        return;
      }
      // measure first: a row the theme has just re-rendered shows its pills
      // again, and --rd-seg-w must count them before the fit test does
      root.dispatchEvent(new CustomEvent('pdp:measure'));
      var changed = false;
      picker.querySelectorAll(':scope > fieldset.product-form__input').forEach(function (fieldset) {
        if (fieldset.querySelector('label.swatch')) return;
        if (getComputedStyle(fieldset).display === 'none') return;
        var radios = Array.prototype.slice.call(fieldset.querySelectorAll('input[type="radio"]'));
        var width = fieldset.clientWidth;
        if (!radios.length || !width) return;
        var converted = fieldset.classList.contains('rd-as-select');
        // a converted row's pills are hidden and no longer measured, so it is
        // tested with the width they had when it converted - otherwise the
        // narrower pills left elsewhere would let it flip back and forth
        var pill = segmentW(fieldset);
        if (converted) pill = Math.max(pill, parseFloat(fieldset.dataset.rdSegW) || 0);
        var fits = radios.length * pill - (radios.length - 1) <= width;
        if (fits && converted) {
          revert(fieldset);
          changed = true;
        } else if (!fits && !converted) {
          fieldset.dataset.rdSegW = pill;
          convert(fieldset, radios);
          changed = true;
        }
      });
      // the pills still showing may size differently without (or with) a row
      if (changed) root.dispatchEvent(new CustomEvent('pdp:measure'));
    }

    apply();
    var later = debounce(apply, 200);
    window.addEventListener('resize', later);
    // the row's own width, which can change without a window resize (a
    // device emulator did exactly that). Width only: a swap changes the
    // picker's height, and reacting to that would call apply for nothing
    if ('ResizeObserver' in window) {
      var lastWidth = picker.clientWidth;
      new ResizeObserver(function () {
        var width = picker.clientWidth;
        if (width === lastWidth) return;
        lastWidth = width;
        later();
      }).observe(picker);
    }
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(apply);
    if ('MutationObserver' in window) {
      new MutationObserver(apply).observe(picker, { childList: true });
    }
  }

  /* ---------- free shipping meter ------------------------------
     Mirrors cart.js:104. The threshold is in SHOP currency while the cart
     total is in presentment currency, so the cart total is normalised back to
     shop currency before comparing, and the remainder is formatted forward
     again by formatMoney. A market's own threshold is in the shown currency
     already: then nothing is converted, and as formatMoney converts anyway
     the remainder goes in divided by the rate. On a cart change the whole
     block is re-rendered from the server, because one of the two exclusions
     (a metafield) is not visible in /cart.js.                    */
  function shippingMeter(root) {
    var meter = root.querySelector('[data-pdp-shipping]');
    if (!meter) return;

    var threshold = parseInt(meter.dataset.threshold, 10) || 0;
    var fill = meter.querySelector('[data-shipping-fill]');
    var text = meter.querySelector('[data-shipping-text]');
    if (!threshold) return;

    function paint(cartTotalPresentment) {
      var cartTotal = cartTotalPresentment;
      // a market's own threshold is in the shown currency: nothing to convert
      var thresholdCurrency = meter.dataset.thresholdCurrency || meter.dataset.shopCurrency;
      var rate = parseFloat(window.Shopify && Shopify.currency && Shopify.currency.rate) || 1;
      var converted = thresholdCurrency !== meter.dataset.targetCurrency;
      if (converted) {
        cartTotal = Math.round(cartTotal / rate);
      }
      var left = Math.max(0, threshold - cartTotal);
      var pct = Math.max(0, Math.min(100, (cartTotal / threshold) * 100));
      if (fill) fill.style.width = pct + '%';
      if (!text) return;
      if (left === 0) {
        text.innerHTML = meter.dataset.reachedText || '';
      } else {
        var tpl = meter.dataset.remainingText || '';
        text.innerHTML = tpl.replace('[amount]', '<strong>' + money(converted ? left : left / rate) + '</strong>');
      }
      // a converted amount waits invisible until now (snippets/pdp-shipping-meter)
      text.removeAttribute('data-pending');
    }

    paint(parseInt(meter.dataset.cartTotal, 10) || 0);
  }

  /* Re-render the meter from the server on a cart change, so the exclusion
     rules are re-evaluated: the custom.no_free_shipping metafield cannot be
     seen from /cart.js, only the _Long_Goods line item property can.

     Bound once per section. It used to be bound inside shippingMeter(), which
     every re-render runs again for the new markup - two more listeners each
     time, so each add doubled the section refetches: 1, 2, 4 ... 422 after ten
     adds in one page view, 386 in flight at once. That flood can crowd out the
     variant refetch a span switch needs (theme-gotchas 41, 42).
     One request at a time: a change while one is out asks for a single pass
     after it. The section id comes from the root, which is there in both
     states - the meter, or the "not eligible" line that carries no data.
     Adds (product form, customizer, pairing drawer) send ajaxProduct:added;
     quantity changes and removals in the cart drawer send cart:update
     (cart.js), the same event the theme's own cart blip listens to. */
  function shippingRefresh(root) {
    var busy = false;
    var again = false;

    function refresh() {
      // a section the theme editor has replaced keeps no listeners of its own
      if (!root.isConnected) return;
      if (!root.querySelector('[data-pdp-shipping], [data-shipping-not-eligible]')) return;
      if (busy) { again = true; return; }
      busy = true;
      fetch(window.location.pathname + '?section_id=' + encodeURIComponent(root.id))
        .then(function (r) { return r.text(); })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var fresh = doc.querySelector('[data-pdp-shipping], [data-shipping-not-eligible]');
          var current = root.querySelector('[data-pdp-shipping], [data-shipping-not-eligible]');
          if (!fresh || !current || !current.parentNode) return;
          current.parentNode.replaceChild(fresh, current);
          shippingMeter(root);             // paint the new markup; binds nothing
        })
        .catch(function () { /* leave the server-rendered state alone */ })
        .then(function () {
          busy = false;
          if (again) { again = false; refresh(); }
        });
    }

    document.addEventListener('ajaxProduct:added', refresh);
    document.addEventListener('cart:update', refresh);
  }

  /* ---------- sticky buy bar -----------------------------------
     Appears once the real buy button has scrolled UP out of view.

     It must not appear while the buy button is still below the fold,
     which is the state you land in below desktop: there the buy panel
     sits under a full-height gallery, so on a 375x812 screen the button
     starts around 1290px down. "Not intersecting" is true in both
     directions, so testing it alone showed the bar on arrival at the
     top. boundingClientRect.top < 0 distinguishes the two: combined
     with !isIntersecting it means the anchor is fully above the
     viewport, i.e. genuinely scrolled past.                        */
  function stickyBar(root) {
    var bar = document.querySelector('[data-pdp-sticky]');
    var anchor = root.querySelector('.product-block__buy_buttons');
    if (!bar || !anchor || !('IntersectionObserver' in window)) return;

    new IntersectionObserver(function (entries) {
      var entry = entries[0];
      var scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      bar.classList.toggle('is-visible', scrolledPast);
    }, { rootMargin: '0px 0px -80px 0px' }).observe(anchor);

    var cta = bar.querySelector('[data-sticky-cta]');
    if (cta) {
      cta.addEventListener('click', function () {
        var submit = root.querySelector('product-form [type="submit"]');
        if (submit && !submit.disabled) return submit.click();
        anchor.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    }

    // mirror the live price and the selected variant summary
    var priceOut = bar.querySelector('[data-sticky-price]');
    var variantOut = bar.querySelector('[data-sticky-variant]');

    function mirrorPrice() {
      if (!priceOut) return;
      var price = root.querySelector('.price .price-item--regular, .price__regular .price-item');
      var txt = price ? price.textContent.trim() : '';
      // renderProductInfo fires TWICE per switch and the first pass renders an
      // empty price wrapper; writing that would blank the bar for ~400 ms.
      if (txt) priceOut.textContent = txt;
    }

    function mirrorVariant() {
      if (!variantOut) return;
      // not a row the page hides (the height span, pdp-spans.liquid)
      var shown = Array.prototype.filter.call(
        root.querySelectorAll('.product-form__input input:checked'),
        function (el) {
          var row = el.closest('fieldset');
          return !row || getComputedStyle(row).display !== 'none';
        }
      );
      var parts = shown.map(function (el) { return (el.value || '').trim(); }).filter(Boolean);
      variantOut.textContent = parts.join(' · ');
    }

    // The radio is checked synchronously, so the variant summary is already
    // correct on change. The PRICE is not - it arrives with the refetch.
    root.addEventListener('change', mirrorVariant);

    /* Same signal as the delivery estimate below: variant:changed is never
       dispatched, and the raw change event fires BEFORE the refetch lands, so
       mirroring on it pinned the bar to the previous variant's price (268 cm
       selected, bar still reading the 132 cm price). Watch the node
       renderProductInfo actually rewrites instead. Coalesced with setTimeout,
       not rAF - rAF is paused in a background tab and would latch `queued`. */
    var queued = false;
    var priceNode = document.getElementById('price-' + root.id);
    if (priceNode && 'MutationObserver' in window) {
      new MutationObserver(function () {
        if (queued) return;
        queued = true;
        setTimeout(function () { queued = false; mirrorPrice(); }, 0);
      }).observe(priceNode, {
        childList: true, subtree: true, attributes: true, attributeFilter: ['class']
      });
    }

    mirrorPrice();
    mirrorVariant();
  }

  /* ---------- FAQ accordion ------------------------------------
     <details> keeps working without JS; this only adds the height
     transition, so a closed panel is still readable to crawlers. */
  function faq(root) {
    var DUR = 340;
    root.querySelectorAll('[data-pdp-faq-item]').forEach(function (item) {
      var body = item.querySelector('[data-pdp-faq-body]');
      var summary = item.querySelector('summary');
      if (!body || !summary) return;
      var timer;

      summary.addEventListener('click', function (e) {
        e.preventDefault();
        clearTimeout(timer);

        if (item.open) {
          body.style.height = body.scrollHeight + 'px';
          requestAnimationFrame(function () { body.style.height = '0px'; });
          timer = setTimeout(function () {
            item.open = false;
            body.style.height = '';
          }, DUR);
        } else {
          item.open = true;
          body.style.height = '0px';
          requestAnimationFrame(function () {
            body.style.height = body.scrollHeight + 'px';
          });
          timer = setTimeout(function () { body.style.height = ''; }, DUR);
        }
      });
    });
  }

  /* ---------- help-row link that opens an accordion item --------
     A help-row link keeps its URL setting as its href, which is what
     no-JS visitors get and what a product without the item falls back
     to (the item is skipped when its content is empty). When the item
     is on the page, the click opens it through its own summary, so it
     animates like a normal click, and scrolls it to the top. html's
     scroll-padding-top already clears the fixed header. Modified clicks
     still go to the page, so "open in new tab" works.
     data-pdp-faq-contains (Link 1, the sample) also requires the item
     to link to that URL - otherwise the click follows the href.       */
  function faqLinks(root) {
    root.addEventListener('click', function (e) {
      var link = e.target.closest && e.target.closest('[data-pdp-faq-link]');
      if (!link || e.defaultPrevented || e.button !== 0) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

      // both sides are handleized in Liquid, so the key is a safe token
      var key = link.getAttribute('data-pdp-faq-link');
      var item = root.querySelector('[data-pdp-faq-key="' + key + '"]');
      var summary = item && item.querySelector('summary');
      if (!summary) return;

      var contains = link.getAttribute('data-pdp-faq-contains');
      if (contains && !item.querySelector('a[href*="' + contains.replace(/["\\]/g, '\\$&') + '"]')) return;

      e.preventDefault();
      if (!item.open) summary.click();
      summary.focus({ preventScroll: true });
      item.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  /* ---------- amounts in the shopper's currency -----------------
     A threshold kept in shop currency (snippets/pdp-delivery-facts) cannot
     be formatted in another market's currency in Liquid, so its line waits
     hidden and formatMoney converts it here, as it does for the meter. */
  function moneyLines(root) {
    root.querySelectorAll('[data-pdp-money-line]').forEach(function (line) {
      var span = line.querySelector('[data-pdp-money]');
      var cents = span ? parseInt(span.getAttribute('data-pdp-money'), 10) : 0;
      if (!cents) return;
      span.textContent = money(cents);
      line.hidden = false;
    });
  }

  /* ---------- fabric sample per variant ------------------------
     The help row is not among the nodes the theme swaps on a variant
     change, so a product whose variants have samples of their own carries a
     map (snippets/pdp-help-row.liquid): variant id -> sample, plus the
     product's as "default". The sample link is pointed at the current
     variant's sample whenever it is about to be used - hovered, focused,
     touched or clicked, before the drawer's own listeners on document read
     it. Only an exact fabric (a variant) opens the drawer; a variant without
     one gets the product's, else the general sample page as a plain link.
     Nothing to do without a map, which is every product until the variants
     have the field. */
  function sampleLink(root) {
    var link = root.querySelector('[data-pdp-sample]');
    var source = root.querySelector('[data-pdp-sample-map]');
    if (!link || !source) return;
    var map;
    try { map = JSON.parse(source.textContent); } catch (e) { return; }

    function sync() {
      var form = document.getElementById('product-form-' + root.id);
      var input = form && form.querySelector('input[name="id"]');
      var sample = (input && map[input.value]) || map['default'];
      if (!sample || !sample.url) return;
      link.setAttribute('href', sample.url);
      // the accordion the page may have been rendered with is not ours to
      // restore: an exact fabric opens the drawer, anything else is a link
      link.removeAttribute('data-pdp-faq-link');
      link.removeAttribute('data-pdp-faq-contains');
      if (sample.open && sample.variant) {
        link.setAttribute('data-pairing-open', sample.open);
        link.setAttribute('data-pairing-variant', sample.variant);
        link.setAttribute('aria-haspopup', 'dialog');
      } else {
        // no exact fabric: the general sample page, as a plain link
        link.removeAttribute('data-pairing-open');
        link.removeAttribute('data-pairing-variant');
        link.removeAttribute('aria-haspopup');
      }
    }
    ['pointerover', 'focusin', 'touchstart'].forEach(function (type) {
      link.addEventListener(type, sync, { passive: true });
    });
    link.addEventListener('click', sync);
  }

  /* ---------- colour swatch hover label ------------------------
     Hovering a colour swatch previews that colour's name in the
     group label, then restores the selected one. Sibling swatches
     are bound once. Colour chips from a variant option are
     delegated from the panel, because the theme rewrites the
     picker on every variant change; their label is the legend's
     <strong data-variant-legend>, and "selected" is re-read from
     the checked radio each time rather than cached.              */
  function swatchLabel(root) {
    var labelEl = root.querySelector('.product-block__sibling_products strong');
    if (labelEl) {
      var selected = labelEl.textContent.trim();

      root.querySelectorAll('.rd-options .sibling-product').forEach(function (link) {
        var nameEl = link.querySelector('.visually-hidden');
        if (!nameEl) return;
        var name = nameEl.textContent.trim();
        var show = function () { labelEl.textContent = name; };
        var reset = function () { labelEl.textContent = selected; };
        link.addEventListener('mouseenter', show);
        link.addEventListener('mouseleave', reset);
        link.addEventListener('focus', show);
        link.addEventListener('blur', reset);
      });
    }

    var panel = root.querySelector('.rd-options');
    if (!panel) return;

    // the radio behind a chip: from the label (pointer) or the radio itself
    // (keyboard - the theme keeps it focusable but visually hidden)
    function chipRadio(el) {
      if (!el || !el.closest) return null;
      var label = el.closest('.product-form__input label.swatch');
      if (label) return document.getElementById(label.htmlFor);
      var next = el.nextElementSibling;
      if (el.type === 'radio' && next && next.matches('label.swatch')) return el;
      return null;
    }
    function legendOf(radio) {
      var fieldset = radio.closest('fieldset');
      return fieldset && fieldset.querySelector('[data-variant-legend]');
    }
    function show(e) {
      var radio = chipRadio(e.target);
      var legend = radio && legendOf(radio);
      if (legend) legend.textContent = radio.value;
    }
    function reset(e) {
      var radio = chipRadio(e.target);
      if (!radio) return;
      // still on this chip, or moving onto a neighbour that names itself
      var next = chipRadio(e.relatedTarget);
      if (next && next.name === radio.name) return;
      var legend = legendOf(radio);
      var checked = radio.closest('fieldset').querySelector('input[type="radio"]:checked');
      if (legend && checked) legend.textContent = checked.value;
    }
    panel.addEventListener('mouseover', show);
    panel.addEventListener('mouseout', reset);
    panel.addEventListener('focusin', show);
    panel.addEventListener('focusout', reset);
  }

  /* ---------- colour swatch hover -> hero preview ---------------
     Hovering a swatch lays that colour's own main image over the hero,
     so the range can be compared without a page hop per colour. The
     image comes from data-preview, which pdp-colour-swatches.liquid
     only emits on the OTHER colours - the current one is already on
     screen, so its swatch stays inert.

     A layer on top of the slider rather than a src swap on the slide:
     the theme owns that DOM (scroll position, srcset, lazy loading, the
     zoom modal's item ids), so nothing has to be restored on the way out
     and a mid-hover variant change cannot strand a foreign image in the
     gallery. The stylesheet aligns the layer with the hero box.

     Pointer-driven and hover-capable only. On touch there is no hover,
     and the synthetic pointerenter a tap fires would flash the preview
     for the few frames before the navigation it belongs to.         */
  function swatchPreview(root) {
    var slider = root.querySelector('.product-media-slider');
    var row = root.querySelector('.product-block__sibling_products');
    if (!slider || !row) return;
    if (!window.matchMedia || !matchMedia('(hover: hover) and (pointer: fine)').matches) return;

    var layer = null;
    // bumped on every show/hide, so a slow image that lands after the pointer
    // has moved on is dropped instead of overwriting the newer one
    var turn = 0;

    function overlay() {
      if (!layer || !layer.isConnected) {
        layer = document.createElement('img');
        layer.className = 'rd-swatch-preview';
        layer.alt = '';
        layer.setAttribute('aria-hidden', 'true');
        layer.setAttribute('draggable', 'false');
        slider.appendChild(layer);
      }
      return layer;
    }

    function show(src) {
      if (!src) return;
      var el = overlay();
      var mine = ++turn;
      var pre = new Image();
      var apply = function () {
        pre.onload = null;
        if (mine !== turn) return;
        el.src = src;
        el.classList.add('is-visible');
      };
      // only reveal once the bytes are in, otherwise the first hover of each
      // colour fades in an empty box while the image is still downloading
      pre.onload = apply;
      pre.src = src;
      if (pre.complete) apply();
    }

    function hide() {
      turn++;
      if (layer) layer.classList.remove('is-visible');
    }

    // Warm the previews once the pointer reaches the buy panel. Measured cold
    // on the dev store, the first hover of a colour otherwise waits ~1s for its
    // image while the swatch sits there doing nothing; by the time the pointer
    // has crossed the panel to the swatch row the bytes are usually in. Nothing
    // is fetched for a visitor who only ever touches the gallery.
    var panel = row.closest('.rd-options') || row;
    panel.addEventListener('pointerenter', function () {
      row.querySelectorAll('a.sibling-product[data-preview]').forEach(function (link) {
        new Image().src = link.dataset.preview;
      });
    }, { once: true });

    row.querySelectorAll('a.sibling-product[data-preview]').forEach(function (link) {
      link.addEventListener('pointerenter', function (e) {
        if (e.pointerType && e.pointerType !== 'mouse') return;
        show(link.dataset.preview);
      });
      link.addEventListener('pointerleave', hide);
      link.addEventListener('focus', function () { show(link.dataset.preview); });
      link.addEventListener('blur', hide);
    });

    // a fast sweep off the row can outrun a swatch's own pointerleave
    row.addEventListener('pointerleave', hide);
  }

  /* ---------- reserve the shop-the-look slot -------------------
     The Cevoid gallery injects its markup about 400ms after load,
     which grows the document by its own height under anyone already
     reading near it. Measured at 167px on a 375px viewport.

     Reserving the height up front and releasing it once the app has
     rendered keeps the document height constant across the injection,
     without leaving a gap on products whose gallery is empty - the
     block's settings hide it in that case, and the release still runs
     on the timeout.                                                */
  var SHOP_SLOT_RESERVE = 167;

  function reserveShopSlot(root) {
    var shop = root.querySelector('.rd-shop');
    var slot = shop && shop.querySelector('.shopify-block');
    if (!shop || !slot) return;

    // The app ships a server-rendered shell (its own block div plus
    // style/script tags) and fills it later, so child count says
    // nothing - only the rendered height does.
    if (slot.getBoundingClientRect().height >= SHOP_SLOT_RESERVE) return;

    // Reserve on the section, leaving the slot free to be measured.
    var base = Math.round(shop.getBoundingClientRect().height);
    shop.style.minHeight = (base + SHOP_SLOT_RESERVE) + 'px';

    var released = false;
    function release() {
      if (released) return;
      released = true;
      shop.style.minHeight = '';
    }

    if ('ResizeObserver' in window) {
      var ro = new ResizeObserver(function () {
        // most of the way there is enough; the last pixels are the
        // app's own images settling and are not worth holding for
        if (slot.getBoundingClientRect().height < SHOP_SLOT_RESERVE * 0.6) return;
        ro.disconnect();
        release();
      });
      ro.observe(slot);
      setTimeout(function () { ro.disconnect(); release(); }, 5000);
    } else {
      setTimeout(release, 5000);
    }
  }

  /* ---------- colour switch continuity -------------------------
     Each colour is its own product, so a swatch is a real <a> and
     switching colour is a full navigation - the browser lands the new
     page at scroll 0 and the shopper loses the row they were reading.

     This keeps the swatch row at the same viewport offset across the
     hop, so the page appears to stay put and only the contents change.
     It is a progressive enhancement: with JS off, or when anything
     below is missing, the link behaves exactly as before.

     Only applied to a fresh 'navigate'. On back/forward the browser has
     its own (better) scroll restoration and must be left alone.     */
  function siblingNav(root) {
    var KEY = 'pdp:sibling-nav';
    var row = root.querySelector('.product-block__sibling_products');
    if (!row) return;

    root.addEventListener('click', function (e) {
      var link = e.target.closest && e.target.closest('a.sibling-product');
      if (!link || link.classList.contains('current-product')) return;
      try {
        sessionStorage.setItem(KEY, JSON.stringify({
          offset: Math.round(row.getBoundingClientRect().top),
          t: Date.now()
        }));
      } catch (err) { /* private mode: fall back to a normal navigation */ }

      // Carry a ?view= override across the hop. Only present while
      // previewing the template before the sibling products have their
      // template assigned in the admin; in production there is no
      // param and the href is left untouched.
      //
      // Built through the URL API rather than string concatenation because
      // the href ends in the #MainContent fragment - appending "?view=..."
      // to that would land inside the fragment and be lost.
      var view = new URLSearchParams(location.search).get('view');
      if (!view) return;
      try {
        var next = new URL(link.href, location.href);
        if (!next.searchParams.has('view')) {
          next.searchParams.set('view', view);
          link.href = next.toString();
        }
      } catch (err) { /* leave the href as authored */ }
    });

    var nav = performance.getEntriesByType && performance.getEntriesByType('navigation')[0];
    if (nav && nav.type !== 'navigate') return;

    var raw = null;
    try {
      raw = sessionStorage.getItem(KEY);
      sessionStorage.removeItem(KEY);
    } catch (err) { return; }
    if (!raw) return;

    var saved;
    try { saved = JSON.parse(raw); } catch (err) { return; }
    // only honour a hop that just happened, never a stale tab
    if (!saved || typeof saved.offset !== 'number' || Date.now() - saved.t > 10000) return;

    // Stop the moment the shopper takes over; never fight a real scroll.
    var userMoved = false;
    ['wheel', 'touchstart', 'keydown'].forEach(function (ev) {
      window.addEventListener(ev, function () { userMoved = true; }, { once: true, passive: true });
    });

    function place() {
      if (userMoved) return;
      var y = row.getBoundingClientRect().top + window.pageYOffset - saved.offset;
      // The theme sets scroll-behavior:smooth on <html>, so a plain
      // scrollTo here animates - which both looks like the lurch this
      // is meant to remove and loses races against the browser's own
      // scroll-to-top on a fresh navigation. Force it to jump.
      var docEl = document.documentElement;
      var prev = docEl.style.scrollBehavior;
      docEl.style.scrollBehavior = 'auto';
      window.scrollTo(0, Math.max(0, Math.round(y)));
      docEl.style.scrollBehavior = prev;
    }

    // The gallery above the row has no final height until its images
    // decode, so a single pass lands short and then visibly corrects -
    // the same lurch from the other direction. Re-place on every change
    // in document height until it holds still, which converges during
    // load and is therefore never seen.
    place();
    var deadline = Date.now() + 2000;
    var lastHeight = -1;
    (function settle() {
      var h = document.documentElement.scrollHeight;
      if (h !== lastHeight) {
        lastHeight = h;
        place();
      }
      if (!userMoved && Date.now() < deadline) requestAnimationFrame(settle);
    })();
    window.addEventListener('load', place, { once: true });
  }

  /* ---------- the shopper's own variant change ----------------
     On a made-to-measure page, a variant change with the width or height
     filled in opens two windows in which Add takes a wrong line (theme
     note 46, measured on live 2026-10-03 on both templates and the old
     page alike):
     - while the refetch is out, every customizer instance re-reads the
       filled price as its base and Add is enabled: the OLD variant goes
       in at (n+1) x its price, 3x on the first change after load
     - right after the swap the theme enables Add and checks the now empty
       fields only 100 ms later (checkCustomizerFields): the new variant,
       0 kr, goes in without measurements
     The swap empties the fields anyway; here they are emptied at the
     click instead, as a shopper clearing them would. The customizer then
     locks Add (disabled and aria-disabled) and the theme only ever lifts
     `disabled`, so Add stays locked until the new fields are filled. The
     span switch (pdp-measure.js) empties them the same way before its own
     variant change, so it passes here with nothing left to empty.
     Capture on the root: before the theme's handler on variant-selects
     starts the refetch. The customizer itself is not called.

     A switch to a variant with no measurements (Måttillverkad back to
     250 cm on a curtain, to 200 cm on Hotellskena) has no new fields to
     fill, and the lock stayed: Add looked ready and did nothing (live
     2026-10-03 to 2026-10-12). So on every variant change: when the theme
     next lifts `disabled` (the new variant is in and buyable) and it has
     no measurement fields, the leftover aria-disabled goes 150 ms later,
     after the theme's own checkCustomizerFields - as the customizer lifts
     it itself for a product without fields. Every change, not only the
     ones that emptied a field: via a sold-out value the lift comes one
     change later. Only that lift counts, never the submit's own
     aria-disabled while an add is on its way (class loading). A
     made-to-measure variant keeps the customizer's lock until its fields
     are filled.                                                    */
  function variantGuard(root) {
    var watch = null;
    var watchEnd = null;
    function hasFields() {
      return ['max-width-', 'max-height-', 'mounting-height-'].some(function (prefix) {
        return document.getElementById(prefix + root.id);
      });
    }
    function releaseLeftoverLock() {
      var form = document.getElementById('product-form-' + root.id);
      var button = form && form.querySelector('[name="add"]');
      if (!button || !('MutationObserver' in window)) return;
      var stop = function () {
        if (watch) watch.disconnect();
        watch = null;
        clearTimeout(watchEnd);
      };
      stop();
      watch = new MutationObserver(function () {
        if (button.disabled) return;
        stop();
        if (hasFields() || button.getAttribute('aria-disabled') !== 'true') return;
        setTimeout(function () {
          if (!button.disabled && !hasFields() && !button.classList.contains('loading') &&
              button.getAttribute('aria-disabled') === 'true') {
            button.removeAttribute('aria-disabled');
          }
        }, 150);
      });
      watch.observe(button, { attributes: true, attributeFilter: ['disabled'] });
      watchEnd = setTimeout(stop, 15000);
    }
    root.addEventListener('change', function (e) {
      var picker = document.getElementById('variant-selects-' + root.id);
      var target = e.target;
      if (!picker || !target || !picker.contains(target)) return;
      if (target.tagName !== 'INPUT' && target.tagName !== 'SELECT') return;
      ['max-width-', 'max-height-'].forEach(function (prefix) {
        var field = document.getElementById(prefix + root.id);
        if (!field || field.value === '') return;
        field.value = '';
        field.dispatchEvent(new Event('input', { bubbles: true }));
        field.dispatchEvent(new Event('change', { bubbles: true }));
      });
      releaseLeftoverLock();
    }, true);
  }

  /* ---------- boot --------------------------------------------- */
  function init(root) {
    // once per section root; the theme editor's section reload brings a new one
    if (!root || root.dataset.pdpReady) return;
    root.dataset.pdpReady = 'true';
    segmentWidth(root);
    // the dropdown rows right after the pills are measured, then
    // pdp-measure.js where the section loads it (the height spans and the
    // line under the measurements), in the order solskydd has always run them
    optionSelects(root);
    if (PDP.measure) PDP.measure(root);
    variantGuard(root);
    shippingMeter(root);
    shippingRefresh(root);
    stickyBar(root);
    reserveShopSlot(root);
    siblingNav(root);
    faq(root);
    faqLinks(root);
    sampleLink(root);
    moneyLines(root);
    swatchLabel(root);
    deliveryEstimate(root);
    swatchPreview(root);
    // pdp-accessories.js, where the section has an upsell_products block
    if (PDP.accessories) PDP.accessories(root);
  }

  /* ---------- delivery date estimate --------------------------
     Turns the store's stated lead times into real dates:
     "Leverans 18-24 sep" rather than "inom 2-6 arbetsdagar".

     Client-side on purpose. The same line rendered in Liquid would be
     baked into Shopify's full-page cache and could be served a day stale;
     the snippet therefore ships a plain business-day sentence and this
     overwrites it on load. Dates are resolved in Europe/Stockholm, not the
     shopper's zone - the promise is about when the warehouse ships.       */
  function deliveryEstimate(root) {
    var box = root.querySelector('[data-pdp-delivery]');
    if (!box) return;
    var out = box.querySelector('[data-pdp-delivery-text]');
    if (!out) return;

    var d = box.dataset;
    var num = function (v, fallback) {
      var n = parseInt(v, 10);
      return isNaN(n) ? fallback : n;
    };

    /* Easter Sunday, anonymous Gregorian algorithm. Every moving Swedish
       red day is an offset from it, so this is the only hard part. */
    function easter(year) {
      var a = year % 19, b = Math.floor(year / 100), c = year % 100;
      var dd = Math.floor(b / 4), e = b % 4;
      var f = Math.floor((b + 8) / 25);
      var g = Math.floor((b - f + 1) / 3);
      var h = (19 * a + b - dd - g + 15) % 30;
      var i = Math.floor(c / 4), k = c % 4;
      var l = (32 + 2 * e + 2 * i - h - k) % 7;
      var m = Math.floor((a + 11 * h + 22 * l) / 451);
      var month = Math.floor((h + l - 7 * m + 114) / 31);
      var day = ((h + l - 7 * m + 114) % 31) + 1;
      return new Date(year, month - 1, day);
    }

    var holidayCache = {};
    /* Weekday red days only - Saturdays and Sundays are already excluded, so
       Midsommardagen and Alla helgons dag never need listing. Julafton,
       midsommarafton and nyårsafton are not official holidays but the
       warehouse is shut, and erring long is the safe direction here. */
    function holidays(year) {
      if (holidayCache[year]) return holidayCache[year];
      var key = function (dt) { return dt.getFullYear() + '-' + dt.getMonth() + '-' + dt.getDate(); };
      var e = easter(year);
      var off = function (n) { return new Date(year, e.getMonth(), e.getDate() + n); };
      var list = [
        new Date(year, 0, 1),   // nyårsdagen
        new Date(year, 0, 6),   // trettondedag jul
        off(-2),                // långfredag
        off(1),                 // annandag påsk
        new Date(year, 4, 1),   // första maj
        off(39),                // Kristi himmelsfärdsdag
        new Date(year, 5, 6),   // nationaldagen
        new Date(year, 11, 24), // julafton
        new Date(year, 11, 25), // juldagen
        new Date(year, 11, 26), // annandag jul
        new Date(year, 11, 31)  // nyårsafton
      ];
      // midsommarafton: the Friday that falls on 19-25 June
      for (var day = 19; day <= 25; day++) {
        var cand = new Date(year, 5, day);
        if (cand.getDay() === 5) { list.push(cand); break; }
      }
      var set = {};
      list.forEach(function (dt) { set[key(dt)] = true; });
      holidayCache[year] = { set: set, key: key };
      return holidayCache[year];
    }

    function isBusinessDay(dt) {
      var wd = dt.getDay();
      if (wd === 0 || wd === 6) return false;
      var h = holidays(dt.getFullYear());
      return !h.set[h.key(dt)];
    }

    function addBusinessDays(from, n) {
      var r = new Date(from.getTime()), added = 0;
      while (added < n) {
        r.setDate(r.getDate() + 1);
        if (isBusinessDay(r)) added++;
      }
      return r;
    }

    function snapForward(from) {
      var r = new Date(from.getTime());
      while (!isBusinessDay(r)) r.setDate(r.getDate() + 1);
      return r;
    }

    /* "now" as the warehouse sees it. sv-SE formats as YYYY-MM-DD HH:MM:SS,
       so the parts can be read straight off without a second Date round-trip. */
    function stockholmNow() {
      var parts;
      try {
        parts = new Date().toLocaleString('sv-SE', { timeZone: 'Europe/Stockholm' });
      } catch (err) {
        parts = null;
      }
      var m = parts && parts.match(/^(\d{4})-(\d{2})-(\d{2})\D+(\d{2})/);
      if (!m) {
        var local = new Date();
        return { date: new Date(local.getFullYear(), local.getMonth(), local.getDate()), hour: local.getHours() };
      }
      return {
        date: new Date(+m[1], +m[2] - 1, +m[3]),
        hour: +m[4]
      };
    }

    /* The page's own language (<html lang>, layout/theme.liquid). Swedish
       keeps the hand-built range below; other languages get Intl's range,
       which knows their day/month order ("Oct 3 – 6", "3.–6.10."). */
    var pageLang = (document.documentElement.lang || 'sv').toLowerCase();
    var swedish = pageLang.indexOf('sv') === 0;

    function label(dt) {
      // sv-SE short months carry a trailing period ("18 sep."); drop it
      return dt.toLocaleDateString('sv-SE', { day: 'numeric', month: 'short' }).replace(/\.$/, '');
    }

    function range(a, b) {
      if (!swedish) {
        try {
          var fmt = new Intl.DateTimeFormat(pageLang, { day: 'numeric', month: 'short' });
          if (a.getTime() === b.getTime()) return fmt.format(b);
          return fmt.formatRange ? fmt.formatRange(a, b) : fmt.format(a) + '–' + fmt.format(b);
        } catch (err) { /* unknown language tag: Swedish below */ }
      }
      if (a.getTime() === b.getTime()) return label(b);
      if (a.getMonth() === b.getMonth() && a.getFullYear() === b.getFullYear()) {
        return a.getDate() + '–' + label(b);
      }
      return label(a) + '–' + label(b);
    }

    /* Made to measure is read off the customizer rather than kept in a
       variable: that element IS in the theme's variant swap list, so it is
       always current after a change, while this block never gets swapped.
       data-custom is the first-paint value for products with no customizer. */
    function isCustom() {
      var cz = document.getElementById('customizer-' + root.id);
      if (!cz) return d.custom === 'true';
      if (cz.classList.contains('hidden')) return false;
      return cz.innerHTML.trim().length > 0;
    }

    function render() {
      var now = stockholmNow();
      var start = new Date(now.date.getTime());
      var cutoff = num(d.cutoff, 0);
      if (cutoff > 0 && now.hour >= cutoff) start.setDate(start.getDate() + 1);

      var custom = isCustom();
      // made to measure with the sy upp add-on ticked (assets/sy-upp.js): its
      // days and its own line, from the section root
      var syupp = custom && root.classList.contains('rd-syupp-on') && root.dataset.syuppDelivery;
      // made to measure cut to size in the warehouse (the rails, section
      // setting custom_ships_from_stock): business days like a stock item,
      // with the section's own warehouse days, and the stock wording
      var fromStock = custom && d.customStock === 'true';
      var first, last;
      if (syupp) {
        first = last = snapForward(new Date(start.getFullYear(), start.getMonth(), start.getDate() + num(root.dataset.syuppDays, 7)));
      } else if (fromStock) {
        first = addBusinessDays(start, num(d.customStockMin, 2) + num(d.transitMin, 1));
        last = addBusinessDays(start, num(d.customStockMax, 3) + num(d.transitMax, 3));
      } else if (custom) {
        first = snapForward(new Date(start.getFullYear(), start.getMonth(), start.getDate() + num(d.customMin, 21)));
        last = snapForward(new Date(start.getFullYear(), start.getMonth(), start.getDate() + num(d.customMax, 21)));
      } else {
        first = addBusinessDays(start, num(d.warehouseMin, 1) + num(d.transitMin, 1));
        last = addBusinessDays(start, num(d.warehouseMax, 3) + num(d.transitMax, 3));
      }
      if (last < first) last = first;

      // both labels are the block setting or the theme's translation
      // (products.pdp), same as the shipping meter's; sy upp's is always
      // the theme's. The empty check only guards a missing attribute.
      var tpl = syupp || (custom && !fromStock ? (d.labelCustom || '') : (d.label || ''));
      if (!tpl) return;
      out.innerHTML = tpl.split('[range]').join(range(first, last));
    }

    render();

    /* Nothing dispatches variant:changed in this theme, and the raw change
       event fires before the refetch lands, so neither is usable. The price
       and customizer nodes are rewritten by renderProductInfo on every
       variant change - watching them is the one reliable signal. Coalesced
       into a frame because that swap writes several nodes in a row. */
    var queued = false;
    var recalc = function () {
      if (queued) return;
      queued = true;
      // setTimeout rather than requestAnimationFrame: rAF is paused in a
      // background tab, which would leave `queued` latched true and kill
      // every later recalculation until the tab is looked at again.
      setTimeout(function () { queued = false; render(); }, 0);
    };
    ['price-', 'customizer-'].forEach(function (prefix) {
      var node = document.getElementById(prefix + root.id);
      if (!node) return;
      new MutationObserver(recalc).observe(node, {
        childList: true, subtree: true, attributes: true, attributeFilter: ['class']
      });
    });
    // the sy upp add-on ticked or unticked
    root.addEventListener('syupp:change', recalc);
  }

  function boot() {
    document.querySelectorAll('[data-pdp]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // re-init after a theme editor section reload
  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target.querySelector('[data-pdp]');
    if (root) init(root);
  });
})();
